// components/dashboard/widgets/StatusMonografias.jsx
import React from 'react';
import PropTypes from 'prop-types';
import { Spin, Empty } from 'antd';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { useDashboardData } from '../../../hooks/useDashboardData';

const StatusMonografias = ({ endpoint, filtros }) => {
  const { dados, loading, error } = useDashboardData(endpoint, filtros);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 40 }}>
        <Spin size="large" />
        <p style={{ marginTop: 16, color: '#666' }}>Carregando status...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        padding: '20px', 
        textAlign: 'center', 
        backgroundColor: '#fff2f0',
        border: '1px solid #ffccc7',
        borderRadius: '6px',
        color: '#a8071a'
      }}>
        <p style={{ margin: '0 0 4px 0', fontWeight: 'bold' }}>
          Erro ao carregar status
        </p>
        <small style={{ color: '#cf1322' }}>{error}</small>
      </div>
    );
  }

  if (!dados?.length) {
    return <Empty description="Nenhum dado de status encontrado" />;
  }

  // Cores para diferentes status
  const coresStatus = {
    'RASCUNHO': '#d9d9d9',
    'EM_ANALISE': '#fadb14',
    'APROVADA': '#52c41a',
    'REJEITADA': '#f5222d',
    'CORRECOES_SOLICITADAS': '#fa8c16',
    'SUBMETIDA': '#1890ff',
    'EM_REVISAO': '#722ed1',
    'DEVOLVIDA': '#fa541c',
    'REVISTO': '#722ed1',
    'SEM_STATUS': '#8c8c8c'
  };

  // Função para normalizar nomes de status
  const formatarNomeStatus = (status) => {
    const nomes = {
      'RASCUNHO': 'Rascunho',
      'EM_ANALISE': 'Em Análise',
      'APROVADA': 'Aprovada',
      'REJEITADA': 'Rejeitada',
      'CORRECOES_SOLICITADAS': 'Correções Solicitadas',
      'SUBMETIDA': 'Submetida',
      'EM_REVISAO': 'Em Revisão',
      'DEVOLVIDA': 'Devolvida',
      'REVISTO': 'Revisto',
      'SEM_STATUS': 'Sem Status'
    };
    return nomes[status] || status.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
  };

  // Agrupar dados por status (baseado na estrutura real dos dados)
  const statusAgrupados = dados.reduce((acc, item) => {
    const status = item.status || 'SEM_STATUS';
    
    if (!acc[status]) {
      acc[status] = 0;
    }
    acc[status] += 1; // Cada item conta como 1
    return acc;
  }, {});

  const dadosFormatados = Object.entries(statusAgrupados)
    .map(([status, value]) => ({
      name: formatarNomeStatus(status),
      value,
      status: status,
      color: coresStatus[status] || '#8c8c8c'
    }))
    .sort((a, b) => b.value - a.value); // Ordenar por quantidade

  const total = dadosFormatados.reduce((sum, item) => sum + item.value, 0);

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      const percentual = ((data.value / total) * 100).toFixed(1);
      return (
        <div style={{
          backgroundColor: '#fff',
          padding: '12px',
          border: '1px solid #d9d9d9',
          borderRadius: '6px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
        }}>
          <p style={{ margin: 0, fontWeight: 'bold' }}>
            {data.payload.name}
          </p>
          <p style={{ margin: '4px 0 0 0', color: data.payload.color }}>
            Quantidade: {data.value} ({percentual}%)
          </p>
        </div>
      );
    }
    return null;
  };

  // PropTypes para CustomTooltip
  CustomTooltip.propTypes = {
    active: PropTypes.bool,
    payload: PropTypes.arrayOf(PropTypes.shape({
      value: PropTypes.number,
      payload: PropTypes.shape({
        name: PropTypes.string,
        color: PropTypes.string
      })
    }))
  };

  const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, value }) => {
    if (percent < 0.05) return null; // Não mostrar label se muito pequeno
    
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text 
        x={x} 
        y={y} 
        fill="white" 
        textAnchor={x > cx ? 'start' : 'end'} 
        dominantBaseline="central"
        fontSize={12}
        fontWeight="bold"
      >
        {value}
      </text>
    );
  };

  // PropTypes para renderCustomLabel
  renderCustomLabel.propTypes = {
    cx: PropTypes.number,
    cy: PropTypes.number,
    midAngle: PropTypes.number,
    innerRadius: PropTypes.number,
    outerRadius: PropTypes.number,
    percent: PropTypes.number,
    value: PropTypes.number
  };

  return (
    <div style={{ height: 350 }}>
      {/* Resumo no topo */}
      <div style={{ 
        textAlign: 'center', 
        marginBottom: 16,
        padding: '8px',
        backgroundColor: '#fafafa',
        borderRadius: '4px'
      }}>
        <strong style={{ fontSize: 16, color: '#1890ff' }}>
          {total} Monografias
        </strong>
      </div>

      <ResponsiveContainer>
        <PieChart>
          <Pie
            data={dadosFormatados}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={renderCustomLabel}
            outerRadius={80}
            innerRadius={40}
            fill="#8884d8"
            dataKey="value"
          >
            {dadosFormatados.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          <Legend 
            verticalAlign="bottom" 
            height={36}
            iconType="circle"
            wrapperStyle={{ fontSize: '12px' }}
          />
        </PieChart>
      </ResponsiveContainer>

      {/* Lista de status com números */}
      <div style={{ 
        marginTop: 8,
        fontSize: 11,
        color: '#666',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
        justifyContent: 'center'
      }}>
        {dadosFormatados.map((item, index) => (
          <span key={index} style={{ 
            display: 'flex', 
            alignItems: 'center',
            gap: 4
          }}>
            <div style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: item.color
            }} />
            {item.name}: {item.value}
          </span>
        ))}
      </div>
    </div>
  );
};

// PropTypes
StatusMonografias.displayName = 'StatusMonografias';
StatusMonografias.propTypes = {
  endpoint: PropTypes.string.isRequired,
  filtros: PropTypes.object
};

StatusMonografias.defaultProps = {
  filtros: {}
};

export default StatusMonografias;